<template>
  <div class="flex w-full flex-col">
    <div class="bg-surface-container-high" :class="[expanded && 'sticky top-0 z-20']">
      <div
        class="divide-outline-variant relative flex h-16 flex-row items-center divide-x overflow-hidden transition-all"
        :class="[
          !selected && 'bg-surface-container-low rounded-medium',
          selected && 'bg-secondary-container rounded-large',
          expanded && 'rounded-b-extra-small!',
        ]"
      >
        <button
          class="group relative h-full overflow-hidden px-2"
          :disabled="disabled"
          @click="emits('toggleSelected')"
        >
          <BaseRadioButton v-if="singleChoice" :is-checked="selected" :disabled="disabled" />
          <BaseCheckbox v-else :is-checked="selected" :disabled="disabled" />
          <BaseStateLayer v-if="!disabled" />
        </button>
        <button
          class="group relative flex h-full grow flex-row items-center overflow-hidden px-4 text-left"
          :class="[disabled && 'opacity-70']"
          :disabled="disabled"
          @click="toggleExpanded"
        >
          <BaseIcon class="text-on-surface-variant mr-2.5">{{ feedbackTopic.icon }}</BaseIcon>
          <div class="flex grow flex-col">
            <span class="text-body-large text-on-surface">{{ feedbackTopic.title }}</span>
            <span v-if="supportingText" class="text-body-medium text-on-surface-variant">{{
              supportingText
            }}</span>
          </div>
          <span class="text-label-medium text-on-surface-variant">
            {{ numberOfQuestions }} {{ $t('common.question', { count: numberOfQuestions }) }}</span
          >
          <BaseIcon
            v-if="!disabled"
            class="text-on-surface-variant ml-1"
            :class="[expanded && 'rotate-180']"
            >keyboard_arrow_down</BaseIcon
          >
          <BaseStateLayer v-if="!disabled" />
        </button>
      </div>
    </div>
    <BaseAnimatedExtend :is-extended="expanded">
      <div class="pt-0.5">
        <div
          class="bg-surface-container-low rounded-t-extra-small w-full pt-4"
          :class="[!selected && 'rounded-b-medium', selected && 'rounded-b-large']"
        >
          <div class="flex flex-col gap-2 px-4 pb-4">
            <span class="text-body-medium text-on-surface-variant">{{
              feedbackTopic.excerpt
            }}</span>
            <div class="flex w-full flex-row justify-end">
              <BaseTextButton
                color="filled"
                @click="emits('toggleSelected')"
                :disabled="disabled"
                :toggle="selected"
                :selected="selected"
                :icon="selected ? 'check' : undefined"
                >{{ selected ? $t('common.selected') : $t('common.select') }}</BaseTextButton
              >
            </div>
          </div>
          <ul class="flex flex-col gap-3">
            <li v-for="questionGroup in feedbackTopic.questionGroups" :key="questionGroup.id">
              <div
                class="flex w-full flex-row items-center justify-between gap-2 overflow-hidden px-4"
              >
                <h4 class="text-title-medium shrink grow overflow-hidden text-ellipsis">
                  <template v-if="questionGroup.title">
                    {{ questionGroup.title }}
                  </template>

                  <template v-else>
                    {{
                      $t('questionType.' + questionGroup.feedbackType, {
                        count: questionGroup.questions.length,
                      })
                    }}
                  </template>
                </h4>
                <div class="flex flex-col items-end">
                  <span
                    v-if="questionGroup.title"
                    class="text-body-small text-on-surface-variant ml-2 flex shrink gap-1 overflow-hidden whitespace-nowrap"
                  >
                    {{
                      $t('questionType.' + questionGroup.feedbackType, {
                        count: questionGroup.questions.length,
                      })
                    }}
                  </span>
                  <span
                    v-if="questionGroup.feedbackType === FeedbackType.ScaleBased"
                    class="text-body-small text-on-surface-variant ml-2 flex shrink gap-1 overflow-hidden whitespace-nowrap"
                  >
                    <span class="overflow-hidden text-ellipsis">
                      {{ questionGroup.scale.items[0]!.label }}
                    </span>
                    <span> - </span>
                    <span class="overflow-hidden text-ellipsis">
                      {{ questionGroup.scale.items[questionGroup.scale.items.length - 1]!.label }}
                    </span>
                  </span>
                </div>
              </div>
              <ul class="divide-outline-variant flex flex-col gap-0.5 divide-y">
                <li
                  v-for="question in questionGroup.questions"
                  :key="question.id"
                  class="text-body-medium flex flex-row px-4 py-2"
                >
                  <span class="text-outline w-6 shrink-0">
                    {{ getQuestionIndex(question.id) }}
                  </span>
                  <p class="">
                    {{ question.content }}
                  </p>
                </li>
              </ul>
            </li>
          </ul>
        </div>
      </div>
    </BaseAnimatedExtend>
  </div>
</template>

<script setup lang="ts">
  import BaseCheckbox from '../base/checkbox/BaseCheckbox.vue'
  import BaseIcon from '../base/icon/BaseIcon.vue'
  import { computed } from 'vue'
  import type { StructuredFeedbackTemplate } from '@/modules/survey/models/structured-feedback-template.ts'
  import BaseAnimatedExtend from '../base/animated-extend/BaseAnimatedExtend.vue'
  import { FeedbackType } from '@/core/models/feedback-type.ts'
  import BaseTextButton from '../base/button/text-button/BaseTextButton.vue'
  import BaseStateLayer from '../base/state-layer/BaseStateLayer.vue'
  import BaseRadioButton from '../base/radio-button/BaseRadioButton.vue'

  const props = defineProps<Props>()

  interface Props {
    supportingText?: string
    feedbackTopic: StructuredFeedbackTemplate
    expanded: boolean
    selected: boolean
    disabled: boolean
    singleChoice?: boolean
  }

  const emits = defineEmits<{
    (e: 'toggleSelected'): void
    (e: 'toggleExpanded'): void
  }>()

  const numberOfQuestions = computed(
    () =>
      props.feedbackTopic.questionGroups.flatMap((questionGroup) => questionGroup.questions).length,
  )

  const allQuestions = computed(() =>
    props.feedbackTopic.questionGroups.flatMap((questionGroup) => questionGroup.questions),
  )

  const getQuestionIndex = (questionId: string) => {
    return allQuestions.value.findIndex((question) => question.id === questionId) + 1
  }

  const toggleExpanded = () => {
    if (props.disabled) return
    emits('toggleExpanded')
  }
</script>
